import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  adminLogout,
  startAdminTotpEnrollment,
  completeAdminTotpEnrollment,
  sendAdminEmailVerification,
} from '../services/firebase';

/**
 * Hard block on the admin panel until Firebase Auth MFA is real for this session.
 *  - needsEnrollment=true:
 *      - emailVerified=false -> must verify email first (Firebase requires it for MFA).
 *      - emailVerified=true  -> scan a TOTP QR with an authenticator app and enroll.
 *  - needsEnrollment=false -> a factor exists but this sign-in skipped MFA -> re-login.
 * The 6-digit challenge at login is handled by Firebase's normal flow (Login.js).
 */
export default function AdminMfaRequired({ user, needsEnrollment, emailVerified, onEnrolled }) {
  const [secret, setSecret] = useState(null);
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [initErr, setInitErr] = useState('');
  const [enrolledDone, setEnrolledDone] = useState(false);

  const beginTotp = useCallback(async () => {
    setBusy(true);
    setInitErr('');
    try {
      const r = await startAdminTotpEnrollment();
      setSecret(r.secret);
      setOtpauthUrl(r.otpauthUrl);
      setSecretKey(r.secretKey);
    } catch (e) {
      setInitErr(e?.message || 'Could not start authenticator setup');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (needsEnrollment && emailVerified && !secret && !enrolledDone) beginTotp();
  }, [needsEnrollment, emailVerified, secret, enrolledDone, beginTotp]);

  const sendVerifyEmail = async () => {
    setBusy(true);
    try {
      await sendAdminEmailVerification();
      toast.success('Verification email sent — open the link, then come back and refresh');
    } catch (e) {
      toast.error(e?.message || 'Could not send verification email');
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      await user?.reload?.();
      await onEnrolled?.();
    } finally {
      setBusy(false);
    }
  };

  const verifyTotp = async () => {
    if (code.trim().length < 6) { toast.error('Enter the 6-digit code from your app'); return; }
    setBusy(true);
    try {
      await completeAdminTotpEnrollment(secret, code, 'Authenticator app');
      setEnrolledDone(true);
      await onEnrolled?.();
    } catch (e) {
      toast.error(e?.message || 'That code did not match — try the current one');
    } finally {
      setBusy(false);
    }
  };

  const wrap = {
    minHeight: '100vh', background: '#070c18', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 24,
  };
  const card = {
    width: '100%', maxWidth: 460, background: 'rgba(15,23,42,0.9)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: 32, color: '#e2e8f0',
  };

  // A factor exists but this session did not complete MFA, or enrollment just finished.
  if (!needsEnrollment || enrolledDone) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>🔐 Re-authentication required</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
            {enrolledDone
              ? 'Authenticator app enrolled. Sign out and sign in again — Firebase will ask for a 6-digit code from your app during login.'
              : 'This admin session did not complete 2-step verification. Sign out and sign in again to continue.'}
          </p>
          <p className="muted" style={{ fontSize: 12 }}>Signed in as {user?.email}</p>
          <button
            type="button"
            className="btn-primary-large"
            style={{ marginTop: 16 }}
            onClick={() => adminLogout()}
          >
            Sign out &amp; sign in again ➔
          </button>
        </div>
      </div>
    );
  }

  // No factor + email not verified -> verify email first.
  if (!emailVerified) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>✉️ Verify your email first</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
            Firebase requires a verified email before you can set up 2-step verification.
            We&rsquo;ll send a link to <strong>{user?.email}</strong>. Open it, then come back and refresh.
          </p>
          <button
            type="button"
            className="btn-primary-large"
            style={{ marginTop: 16 }}
            disabled={busy}
            onClick={sendVerifyEmail}
          >
            {busy ? 'Sending…' : 'Send verification email'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: 12 }}
            disabled={busy}
            onClick={recheck}
          >
            I&rsquo;ve verified &mdash; refresh
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: 8 }}
            onClick={() => adminLogout()}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // No factor + email verified -> TOTP enrollment.
  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>🔐 Set up your authenticator app</h2>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          Admin access requires a second factor. Scan this with Google Authenticator,
          Microsoft Authenticator, Authy, or 1Password, then enter the 6-digit code it shows.
        </p>
        <p className="muted" style={{ fontSize: 12, marginBottom: 16 }}>Signed in as {user?.email}</p>

        {initErr ? (
          <>
            <p style={{ color: '#f87171', fontSize: 13 }}>{initErr}</p>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ width: '100%' }}
              disabled={busy}
              onClick={beginTotp}
            >
              Try again
            </button>
          </>
        ) : !otpauthUrl ? (
          <p className="muted" style={{ fontSize: 13 }}>Preparing your setup key…</p>
        ) : (
          <>
            <div style={{ background: '#fff', padding: 12, borderRadius: 12, width: 'fit-content', margin: '0 auto 14px' }}>
              <QRCodeSVG value={otpauthUrl} size={188} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Can&rsquo;t scan? Enter this key manually:</div>
            <code style={{ display: 'block', fontSize: 12.5, wordBreak: 'break-all', background: 'rgba(255,255,255,0.05)', padding: '8px 10px', borderRadius: 8, marginBottom: 16 }}>
              {secretKey}
            </code>

            <label className="field-label">6-DIGIT CODE FROM YOUR APP</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center', fontWeight: 700 }}
              autoFocus
            />
            <button
              type="button"
              className="btn-primary-large"
              style={{ marginTop: 16 }}
              disabled={busy || code.length < 6}
              onClick={verifyTotp}
            >
              {busy ? 'Enrolling…' : 'Verify & enable 2FA →'}
            </button>
          </>
        )}

        <button
          type="button"
          className="btn btn-ghost"
          style={{ width: '100%', marginTop: 12 }}
          onClick={() => adminLogout()}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
